/** The data model's status, in the words a person reads. */
export const modelStatusCopy = {
  preparing: "Getting your data ready for the data model",
  preparingBecause: (reason: string) => `Getting your data ready: ${reason}`,
  building: "Building your data model",
  buildingSince: (ago: string) => `Building your data model, started ${ago}`,
  none: "No data model yet: add a data source",
  noneBecause: (reason: string) => `No data model yet: ${reason}`,
  held: "Your data model needs a look",
  heldBecause: (reason: string) => `Your data model needs a look: ${reason}`,
  failed: "Building your data model failed",
  failedBecause: (reason: string) => `Building your data model failed: ${reason}`,
  fillsIn: "The knowledge graph fills in once it's ready.",
  previousAnswers: "Chat still answers from the previous data model.",
  storyNote: "You can still create a Story: its charts and chat work once your data model is ready.",
  dataSources: "Data sources",
  unreadable: "Magic couldn't read where your data model stands.",
  retry: "Try again",
} as const;

/** The tenant admin's review of a held data-model update. */
export const modelReviewCopy = {
  review: "Review",
  title: "Review the data model update",
  aboutHeading: "About this update",
  about: (counts: string) => `Approve it to publish it, or reject it to keep your current data model. ${counts}`,
  aboutShare: (share: string, counts: string) =>
    `This update would change about ${share} of your data model. Approve it to publish it, or reject it to keep your current data model. ${counts}`,
  counts: (added: number, changed: number, retired: number) => `${added} added, ${changed} changed, ${retired} retired.`,
  loading: "Reading the changes…",
  unreadable: "Magic couldn't read this update's changes.",
  failedHeading: "That didn't work",
  approveFailed: "Magic couldn't approve this update.",
  rejectFailed: "Magic couldn't reject this update.",
  added: (n: number) => `Added (${n})`,
  changed: (n: number) => `Changed (${n})`,
  retired: (n: number) => `Retired (${n})`,
  unnamed: "Unnamed",
  label: (name: string, type: string) => `${name} · ${type}`,
  aspect: { name: "Name", description: "Description", definition: "Definition", source: "Source" },
  oneLine: (aspect: string, text: string) => `${aspect}: ${text}`,
  oldToNew: (aspect: string, before: string, after: string) => `${aspect}: ${before} → ${after}`,
  before: (aspect: string, text: string) => `${aspect} before:\n${text}`,
  after: (aspect: string, text: string) => `${aspect} now:\n${text}`,
  nothing: "none",
  stopsAnswering: "Questions that use it will stop being answered.",
  approve: "Approve",
  reject: "Reject",
  rejectTitle: "Reject this update?",
  rejectMessage: "Your current data model stays, and this same update won't be raised again.",
  rejectMessageRetired:
    "Your current data model stays, and this same update won't be raised again. The new data no longer has the source of these, so questions that use them will stop being answered:",
  rejectConfirm: "Reject update",
  keepReviewing: "Keep reviewing",
  close: "Close",
  more: (hidden: number) => `and ${hidden} more`,
} as const;

/** The plain name of each kind of data-model element. */
export const elementTypeLabels: Record<string, string> = {
  subject_area: "Subject area",
  business_process: "Business process",
  dimension: "Dimension",
  dimension_attribute: "Attribute",
  dimension_relationship: "Relationship",
  hierarchy: "Hierarchy",
  hierarchy_level: "Hierarchy level",
  fact_table: "Fact table",
  fact_measure: "Measure",
  fact_dimension_role: "Dimension role",
  degenerate_dimension: "Attribute",
  row_filter: "Row filter",
  business_term: "Business term",
  business_rule: "Business rule",
  influence_belief: "Influence",
};
